/*
  Kesselkind – globale Skripte (Vanilla JS, keine Bibliotheken)
  - Hilfsfunktionen: Fokus-Falle, Escape-Handling, Event-Bus, Live-Region
  - Web Components: <announcement-bar>, <header-component>, <menu-drawer>
  Komponenten für Warenkorb, Produktformular, Suche usw. liegen in eigenen Dateien.
*/

/* ---------- Fokus-Falle für Drawer und Menüs ---------- */
const FOCUSABLE =
  'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), summary, iframe';

function getFocusableElements(container) {
  return Array.from(container.querySelectorAll(FOCUSABLE)).filter(
    (el) => el.offsetParent !== null || el === document.activeElement
  );
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  removeTrapFocus();
  const elements = getFocusableElements(container);
  const first = elements[0];
  const last = elements[elements.length - 1];

  trapFocusHandlers.keydown = (event) => {
    if (event.key !== 'Tab') return;
    const current = getFocusableElements(container);
    const f = current[0] || first;
    const l = current[current.length - 1] || last;
    if (event.shiftKey && document.activeElement === f) {
      event.preventDefault();
      l.focus();
    } else if (!event.shiftKey && document.activeElement === l) {
      event.preventDefault();
      f.focus();
    }
  };
  document.addEventListener('keydown', trapFocusHandlers.keydown);
  (elementToFocus || first)?.focus({ preventScroll: true });
}

function removeTrapFocus(elementToFocus = null) {
  if (trapFocusHandlers.keydown) document.removeEventListener('keydown', trapFocusHandlers.keydown);
  trapFocusHandlers.keydown = null;
  if (elementToFocus) elementToFocus.focus({ preventScroll: true });
}

/* ---------- Kleiner Event-Bus (z. B. „cart:updated“) ---------- */
const bus = {
  subscribers: {},
  subscribe(name, callback) {
    (this.subscribers[name] ||= []).push(callback);
    return () => {
      this.subscribers[name] = this.subscribers[name].filter((cb) => cb !== callback);
    };
  },
  publish(name, data) {
    (this.subscribers[name] || []).forEach((cb) => cb(data));
  },
};

/* Bildschirmleser-Ansage über die Live-Region im Layout */
function announce(message) {
  const region = document.querySelector('[data-live-region]');
  if (!region) return;
  region.textContent = '';
  window.setTimeout(() => {
    region.textContent = message;
  }, 50);
}

/* Geldbetrag (in Cent) in der aktuellen Kundenwährung formatieren, z. B. „29,00 €“ oder „CHF 29.00“ */
function formatMoney(cents) {
  const currency = window.Shopify?.currency?.active || 'EUR';
  return new Intl.NumberFormat(document.documentElement.lang || 'de', {
    style: 'currency',
    currency,
  }).format(Number(cents) / 100);
}

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

window.theme = Object.assign(window.theme || {}, {
  trapFocus,
  removeTrapFocus,
  bus,
  announce,
  formatMoney,
  prefersReducedMotion,
});

/* ---------- Ankündigungsleiste: wechselt Texte automatisch ---------- */
class AnnouncementBar extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    if (this.slides.length < 2) return;
    this.index = 0;
    this.interval = Number(this.dataset.speed || 5) * 1000;
    this.paused = false;

    this.addEventListener('mouseenter', () => (this.paused = true));
    this.addEventListener('mouseleave', () => (this.paused = false));
    this.addEventListener('focusin', () => (this.paused = true));
    this.addEventListener('focusout', () => (this.paused = false));
    this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(-1));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.go(1));

    if (this.dataset.autoplay === 'true' && !prefersReducedMotion()) {
      this.timer = window.setInterval(() => {
        if (!this.paused && !document.hidden) this.go(1);
      }, this.interval);
    }
  }

  disconnectedCallback() {
    window.clearInterval(this.timer);
  }

  go(step) {
    this.slides[this.index].hidden = true;
    this.index = (this.index + step + this.slides.length) % this.slides.length;
    this.slides[this.index].hidden = false;
  }
}
customElements.define('announcement-bar', AnnouncementBar);

/* ---------- Header: Mega-Menüs (Hover + Tastatur) und Header-Höhe ---------- */
class HeaderComponent extends HTMLElement {
  connectedCallback() {
    this.menus = Array.from(this.querySelectorAll('details[data-mega-menu]'));
    this.menus.forEach((details) => {
      const summary = details.querySelector('summary');
      details.addEventListener('mouseenter', () => this.open(details));
      details.addEventListener('mouseleave', () => this.close(details));
      summary.addEventListener('click', (event) => {
        event.preventDefault();
        details.open ? this.close(details) : this.open(details);
      });
      details.addEventListener('keyup', (event) => {
        if (event.key === 'Escape') {
          this.close(details);
          summary.focus();
        }
      });
      details.addEventListener('focusout', (event) => {
        if (!details.contains(event.relatedTarget)) this.close(details);
      });
    });

    this.setHeight = () => {
      document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
    };
    this.setHeight();
    this.resizeObserver = new ResizeObserver(this.setHeight);
    this.resizeObserver.observe(this);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  open(details) {
    this.menus.forEach((other) => other !== details && this.close(other));
    details.open = true;
    details.querySelector('summary').setAttribute('aria-expanded', 'true');
  }

  close(details) {
    details.open = false;
    details.querySelector('summary').setAttribute('aria-expanded', 'false');
  }
}
customElements.define('header-component', HeaderComponent);

/* ---------- Mobiles Menü als Vollbild-Panel ---------- */
class MenuDrawer extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('details');
    this.summary = this.details.querySelector('summary');
    this.summary.addEventListener('click', (event) => {
      event.preventDefault();
      this.details.open ? this.close() : this.open();
    });
    this.addEventListener('keyup', (event) => event.key === 'Escape' && this.close(true));
    this.querySelectorAll('[data-close-menu]').forEach((el) =>
      el.addEventListener('click', () => this.close(true))
    );
    window.matchMedia('(min-width: 990px)').addEventListener('change', (mq) => mq.matches && this.close());
  }

  open() {
    /* Menü direkt unter der aktuellen Header-Unterkante beginnen lassen */
    const header = this.closest('.site-header');
    if (header) {
      document.documentElement.style.setProperty('--menu-top', `${Math.max(0, header.getBoundingClientRect().bottom)}px`);
    }
    this.details.open = true;
    this.summary.setAttribute('aria-expanded', 'true');
    document.body.classList.add('overflow-hidden');
    trapFocus(this.details, this.details.querySelector('[data-menu-panel] input, [data-menu-panel] a'));
  }

  close(returnFocus = false) {
    if (!this.details.open) return;
    this.details.open = false;
    this.summary.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('overflow-hidden');
    removeTrapFocus(returnFocus ? this.summary : null);
  }
}
customElements.define('menu-drawer', MenuDrawer);

/* ---------- Beträge aus der Shopwährung in die Kundenwährung umrechnen ----------
   Für Schwellenwerte aus den Theme-Einstellungen (z. B. Gratisversand ab 49 €),
   die in der Shopwährung gepflegt werden. Nutzt den Shopify-Wechselkurs der Märkte. */
function convertMoneyElements(root = document) {
  const rate = Number(window.Shopify?.currency?.rate || 1);
  const currency = window.Shopify?.currency?.active;
  if (!currency) return;
  root.querySelectorAll('[data-convert-money]').forEach((el) => {
    const cents = Number(el.dataset.convertMoney) * rate;
    const value = Math.ceil(cents / 100);
    el.textContent = new Intl.NumberFormat(document.documentElement.lang || 'de', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  });
}
window.theme.convertMoneyElements = convertMoneyElements;
document.addEventListener('DOMContentLoaded', () => convertMoneyElements());

/* ---------- Video erst nach Klick laden (YouTube/Vimeo ohne Cookies vorab) ---------- */
class DeferredVideo extends HTMLElement {
  connectedCallback() {
    this.querySelector('button')?.addEventListener(
      'click',
      () => {
        const iframe = document.createElement('iframe');
        iframe.src = this.dataset.src;
        iframe.title = this.dataset.title || '';
        iframe.allow = 'autoplay; encrypted-media; fullscreen; picture-in-picture';
        iframe.allowFullscreen = true;
        iframe.className = 'video-section__iframe';
        this.replaceChildren(iframe);
        iframe.focus();
      },
      { once: true }
    );
  }
}
customElements.define('deferred-video', DeferredVideo);

/* ---------- Slider (Scroll-Snap) mit Vor/Zurück-Buttons ---------- */
class SliderComponent extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[role="list"], ul');
    const section = this.closest('section') || this.parentElement;
    this.prev = section.querySelector('[data-slider-prev]');
    this.next = section.querySelector('[data-slider-next]');
    if (!this.track) return;
    this.prev?.addEventListener('click', () => this.scrollByPage(-1));
    this.next?.addEventListener('click', () => this.scrollByPage(1));
    this.track.addEventListener('scroll', () => this.update(), { passive: true });
    this.update();
  }

  scrollByPage(direction) {
    this.track.scrollBy({
      left: direction * this.track.clientWidth * 0.8,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  update() {
    const max = this.track.scrollWidth - this.track.clientWidth - 2;
    if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
    if (this.next) this.next.disabled = this.track.scrollLeft >= max;
  }
}
customElements.define('slider-component', SliderComponent);

/* ---------- Produktempfehlungen nachladen (Recommendations API) ----------
   data-url: URL mit section_id; data-fallback-url: falls keine Ergänzungsprodukte
   gepflegt sind, werden „ähnliche“ Produkte geladen. */
class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    const load = () => this.load(this.dataset.url, this.dataset.fallbackUrl);
    if (!('IntersectionObserver' in window)) return load();
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        load();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    observer.observe(this);
  }

  async load(url, fallback) {
    try {
      const html = await fetch(url).then((r) => r.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.querySelector('.shopify-section');
      if (content && content.innerHTML.trim()) {
        this.innerHTML = content.innerHTML;
        window.theme.convertMoneyElements?.(this);
      } else if (fallback) {
        this.load(fallback);
      }
    } catch (error) {
      /* Empfehlungen sind optional – Fehler still ignorieren */
    }
  }
}
customElements.define('product-recommendations', ProductRecommendations);

/* ---------- Länder-/Sprachauswahl: Wechsel sofort absenden ---------- */
class LocalizationForm extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('[data-localization-select]').forEach((select) =>
      select.addEventListener('change', () => this.querySelector('form').submit())
    );
  }
}
customElements.define('localization-form', LocalizationForm);

/* ---------- Cookie-Einstellungen erneut öffnen (Shopify-Consent-Banner) ----------
   Jeder Link auf „#cookie-einstellungen“ (z. B. im Footer-Menü „Cookies“) öffnet die
   Einstellungen des Shopify-Cookie-Banners (Einstellungen → Kundendatenschutz). */
document.addEventListener('click', (event) => {
  const link = event.target.closest('a[href$="#cookie-einstellungen"], [data-cookie-preferences]');
  if (!link) return;
  event.preventDefault();
  const open = () => window.privacyBanner?.showPreferences?.();
  if (window.privacyBanner) {
    open();
  } else if (window.Shopify?.loadFeatures) {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], open);
  }
});
